import React, { ReactElement, useContext, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import PasswordField from "../../common/PasswordField/PasswordField";
import BaseAuthPage from "./BaseAuthPage";
import Button from "../../common/Button";
import LinkText from "../../common/LinkText";
import TextField from "../../common/TextField";
import { getApolloContext } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearPendingNewPasswordChallenge,
  setPendingNewPasswordChallenge,
} from "./pendingNewPasswordChallenge";
import { signInHandler } from "./dashboardSignIn";
import { getSignInErrorMessage } from "./authErrors";
import AuthErrorMessage from "./AuthErrorMessage";
import { getSignOutReasonMessage } from "./signOutReasons";

export { signInHandler } from "./dashboardSignIn";

interface LoginInput {
  email: string;
  password: string;
}

const validationSchema = yup.object({
  email: yup.string().required(enumStrings.pages.common.validation.email),
  password: yup.string().required(enumStrings.pages.common.validation.password),
});

const Login = (): ReactElement => {
  const apolloClient = useContext(getApolloContext());
  const navigate = useNavigate();
  const location = useLocation();

  // The dashboard signed the user out by itself: say why (audit 3.1 item 11).
  const [customError, setCustomError] = useState(() =>
    getSignOutReasonMessage(location.search)
  );
  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const initialValues: LoginInput = {
    email: "",
    password: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: LoginInput) => {
      setCustomError("");
      setLoader(true);
      clearPendingNewPasswordChallenge();
      try {
        await signInHandler(
          input.email.trim().toLowerCase(),
          input.password,
          apolloClient,
          (email) => {
            setPendingNewPasswordChallenge(email);
            navigate(RoutePaths.newPassword);
          }
        );
        setButtonDisabled(true);
      } catch (error: unknown) {
        setCustomError(getSignInErrorMessage(error));
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  return (
    <BaseAuthPage logoSubtitle={enumStrings.pages.login.logoSubtitle}>
      <form
        style={{
          width: "100%",
        }}
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <TextField
            id="email"
            label="Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            fullWidth
            helperText={formik.touched.email && formik.errors.email}
          />
          <PasswordField
            id="password"
            fieldName="password"
            label="Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.errors.password}
            touched={formik.touched.password}
            disablePopup
          />

          <AuthErrorMessage message={customError} />
          <Button
            text={enumStrings.pages.login.buttonText}
            disabled={buttonDisabled || loader}
            isLoading={loader}
            fullWidth
            size="large"
            type="submit"
            additionalSx={{
              mt: 4,
            }}
          />
          <LinkText
            to={RoutePaths.forgotPassword}
            text={enumStrings.pages.login.forgotPasswordText}
          />
        </Box>
      </form>
    </BaseAuthPage>
  );
};

export default Login;
